import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { 
  Calendar, Clock, Check, X, AlertTriangle, User, 
  ChevronLeft, ChevronRight, Filter, UserPlus, Plus, Send
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, 
  isSameMonth, isToday, addMonths, subMonths, isSameDay, differenceInDays } from 'date-fns';
import Modal from './Modal';
import './TimeOffManager.css';

function TimeOffManager({ isOpen, onClose }) {
  const {
    state,
    getTimeOffRequests,
    approveTimeOffRequest,
    denyTimeOffRequest,
    addTimeOffRequest,
    getEmployeeById,
    getJobsByDate,
    isOwnTimeOffRequest
  } = useApp();

  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [managerNote, setManagerNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormData, setAddFormData] = useState({
    employeeId: '',
    startDate: '',
    endDate: '',
    type: 'pto',
    reason: '',
    autoApprove: true
  });
  const [submittingFor, setSubmittingFor] = useState(false);

  const activeEmployees = state.employees.filter(e => e.status === 'active');

  const pendingRequests = getTimeOffRequests('pending');
  const approvedRequests = getTimeOffRequests('approved');
  const deniedRequests = getTimeOffRequests('denied');

  const filteredRequests = useMemo(() => {
    switch (filter) {
      case 'pending': return pendingRequests;
      case 'approved': return approvedRequests;
      case 'denied': return deniedRequests;
      default: return state.timeOffRequests;
    }
  }, [filter, pendingRequests, approvedRequests, deniedRequests, state.timeOffRequests]);

  // Get conflicts for a request
  const getConflicts = (request) => {
    const conflicts = [];
    let currentDate = parseISO(request.startDate);
    const lastDate = parseISO(request.endDate);
    
    while (currentDate <= lastDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const dayJobs = state.jobs.filter(j => 
        j.date === dateStr && 
        j.assignedTeam?.includes(request.employeeId)
      );
      
      if (dayJobs.length > 0) {
        conflicts.push({ date: dateStr, jobs: dayJobs });
      }
      
      currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
    }
    
    return conflicts;
  };

  // Calendar data
  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return eachDayOfInterval({ start, end });
  }, [calendarMonth]);

  // Get time off for a specific day
  const getTimeOffForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return approvedRequests.filter(r => 
      r.startDate <= dateStr && r.endDate >= dateStr
    );
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    if (isOwnTimeOffRequest(selectedRequest.id)) {
      alert('You cannot approve your own time off request.');
      return;
    }
    setProcessing(true);
    try {
      await approveTimeOffRequest(selectedRequest.id, managerNote);
      setSelectedRequest(null);
      setManagerNote('');
    } catch (error) {
      console.error('Error approving request:', error);
      alert(error.message || 'Failed to approve request.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedRequest) return;
    if (isOwnTimeOffRequest(selectedRequest.id)) {
      alert('You cannot deny your own time off request.');
      return;
    }
    setProcessing(true);
    try {
      await denyTimeOffRequest(selectedRequest.id, managerNote);
      setSelectedRequest(null);
      setManagerNote('');
    } catch (error) {
      console.error('Error denying request:', error);
      alert(error.message || 'Failed to deny request.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitForEmployee = async (e) => {
    e.preventDefault();
    if (!addFormData.employeeId || !addFormData.startDate) {
      alert('Please select an employee and start date');
      return;
    }
    setSubmittingFor(true);
    try {
      const request = await addTimeOffRequest({
        employeeId: addFormData.employeeId,
        startDate: addFormData.startDate,
        endDate: addFormData.endDate || addFormData.startDate,
        type: addFormData.type,
        reason: addFormData.reason,
        submittedByManager: true
      });
      
      // Auto-approve if checked (but not for own requests)
      let wasAutoApproved = false;
      if (addFormData.autoApprove) {
        if (isOwnTimeOffRequest(request.id)) {
          alert('Time off submitted as pending. You cannot auto-approve your own request — another manager must review it.');
        } else {
          await approveTimeOffRequest(request.id, 'Submitted & approved by manager');
          wasAutoApproved = true;
        }
      }

      setAddFormData({ employeeId: '', startDate: '', endDate: '', type: 'pto', reason: '', autoApprove: true });
      setShowAddForm(false);
      if (!addFormData.autoApprove || wasAutoApproved) {
        alert(`Time off ${wasAutoApproved ? 'submitted & approved' : 'submitted as pending'}!`);
      }
    } catch (error) {
      console.error('Error submitting time off for employee:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingFor(false);
    }
  };

  const getAddFormDaysCount = () => {
    if (!addFormData.startDate) return 0;
    const end = addFormData.endDate || addFormData.startDate;
    return differenceInDays(parseISO(end), parseISO(addFormData.startDate)) + 1;
  };

  const getTypeLabel = (type) => {
    const labels = {
      pto: 'PTO',
      sick: 'Sick',
      personal: 'Personal',
      unpaid: 'Unpaid'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-pending',
      approved: 'status-approved',
      denied: 'status-denied'
    };
    return styles[status] || '';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Time Off Requests" size="large">
      <div className="time-off-manager">
        {/* Stats */}
        <div className="stats-row">
          <div 
            className={`stat-card ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <span className="stat-value">{pendingRequests.length}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div 
            className={`stat-card ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            <span className="stat-value">{approvedRequests.length}</span>
            <span className="stat-label">Approved</span>
          </div>
          <div 
            className={`stat-card ${filter === 'denied' ? 'active' : ''}`}
            onClick={() => setFilter('denied')}
          >
            <span className="stat-value">{deniedRequests.length}</span>
            <span className="stat-label">Denied</span>
          </div>
        </div>

        {/* Add Time Off for Employee */}
        <div className="manager-add-section">
          <button 
            className={`btn ${showAddForm ? 'btn-secondary' : 'btn-primary'} add-for-employee-btn`}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? <><X size={16} /> Cancel</> : <><UserPlus size={16} /> Add Time Off for Employee</>}
          </button>

          {showAddForm && (
            <form onSubmit={handleSubmitForEmployee} className="manager-add-form">
              <div className="form-row">
                <div className="form-group">
                  <label><User size={14} /> Employee *</label>
                  <select
                    value={addFormData.employeeId}
                    onChange={(e) => setAddFormData({ ...addFormData, employeeId: e.target.value })}
                    required
                  >
                    <option value="">Select employee...</option>
                    {activeEmployees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.firstName} {emp.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={addFormData.type}
                    onChange={(e) => setAddFormData({ ...addFormData, type: e.target.value })}
                  >
                    <option value="pto">PTO</option>
                    <option value="sick">Sick Leave</option>
                    <option value="personal">Personal Day</option>
                    <option value="unpaid">Unpaid Leave</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><Calendar size={14} /> Start Date *</label>
                  <input
                    type="date"
                    value={addFormData.startDate}
                    onChange={(e) => setAddFormData({ ...addFormData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><Calendar size={14} /> End Date</label>
                  <input
                    type="date"
                    value={addFormData.endDate}
                    onChange={(e) => setAddFormData({ ...addFormData, endDate: e.target.value })}
                    min={addFormData.startDate}
                  />
                  <span className="form-hint">Leave blank for single day</span>
                </div>
              </div>

              {addFormData.startDate && (
                <div className="days-count-badge">
                  <Clock size={14} />
                  {getAddFormDaysCount()} day{getAddFormDaysCount() !== 1 ? 's' : ''}
                </div>
              )}

              <div className="form-group">
                <label>Reason / Note (optional)</label>
                <input
                  type="text"
                  value={addFormData.reason}
                  onChange={(e) => setAddFormData({ ...addFormData, reason: e.target.value })}
                  placeholder="e.g. Doctor appointment, family event..."
                />
              </div>

              <div className="form-row auto-approve-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={addFormData.autoApprove}
                    onChange={(e) => setAddFormData({ ...addFormData, autoApprove: e.target.checked })}
                  />
                  <span>Auto-approve this request</span>
                </label>
                <button type="submit" className="btn btn-primary" disabled={submittingFor}>
                  {submittingFor ? 'Submitting...' : <><Send size={14} /> Submit</>}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="manager-layout">
          {/* Requests List */}
          <div className="requests-panel">
            <h3>
              <Filter size={16} />
              {filter.charAt(0).toUpperCase() + filter.slice(1)} Requests
            </h3>
            
            {filteredRequests.length === 0 ? (
              <div className="empty-state">
                <Calendar size={32} />
                <p>No {filter} requests</p>
              </div>
            ) : (
              <div className="requests-list">
                {filteredRequests.map(request => {
                  const employee = getEmployeeById(request.employeeId);
                  const conflicts = filter === 'pending' ? getConflicts(request) : [];
                  
                  return (
                    <div 
                      key={request.id}
                      className={`request-card ${selectedRequest?.id === request.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedRequest(request);
                        setManagerNote('');
                      }}
                    >
                      <div className="request-header">
                        <div className="employee-info">
                          <div className="avatar">
                            {employee?.avatar || employee?.firstName?.[0] || '?'}
                          </div>
                          <div>
                            <strong>{employee?.firstName} {employee?.lastName}</strong>
                            <span className={`type-badge ${request.type}`}>
                              {getTypeLabel(request.type)}
                            </span>
                          </div>
                        </div>
                        <span className={`status-badge ${getStatusBadge(request.status)}`}>
                          {request.status}
                        </span>
                      </div>
                      
                      <div className="request-dates">
                        <Calendar size={14} />
                        {format(parseISO(request.startDate), 'MMM d')}
                        {request.endDate !== request.startDate && (
                          <> - {format(parseISO(request.endDate), 'MMM d')}</>
                        )}
                        , {format(parseISO(request.startDate), 'yyyy')}
                      </div>
                      
                      {conflicts.length > 0 && (
                        <div className="conflict-badge">
                          <AlertTriangle size={12} />
                          {conflicts.length} job conflict{conflicts.length > 1 ? 's' : ''}
                        </div>
                      )}
                      
                      {request.reason && (
                        <div className="request-reason">"{request.reason}"</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Calendar Panel */}
          <div className="calendar-panel">
            <div className="calendar-header">
              <button onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}>
                <ChevronLeft size={20} />
              </button>
              <h3>{format(calendarMonth, 'MMMM yyyy')}</h3>
              <button onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}>
                <ChevronRight size={20} />
              </button>
            </div>
            
            <div className="calendar-grid">
              <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>
              
              <div className="calendar-days">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: calendarDays[0].getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="calendar-day empty" />
                ))}
                
                {calendarDays.map(day => {
                  const timeOff = getTimeOffForDay(day);
                  const hasTimeOff = timeOff.length > 0;
                  
                  return (
                    <div 
                      key={day.toISOString()}
                      className={`calendar-day ${isToday(day) ? 'today' : ''} ${hasTimeOff ? 'has-time-off' : ''}`}
                    >
                      <span className="day-number">{format(day, 'd')}</span>
                      {hasTimeOff && (
                        <div className="time-off-dots">
                          {timeOff.slice(0, 3).map(t => {
                            const emp = getEmployeeById(t.employeeId);
                            return (
                              <div 
                                key={t.id} 
                                className="time-off-dot"
                                title={`${emp?.firstName} - ${getTypeLabel(t.type)}`}
                              >
                                {emp?.firstName?.[0]}
                              </div>
                            );
                          })}
                          {timeOff.length > 3 && (
                            <div className="more-dot">+{timeOff.length - 3}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Action Panel for Selected Request */}
        {selectedRequest && filter === 'pending' && (
          <div className="action-panel">
            <h4>Respond to Request</h4>
            {isOwnTimeOffRequest(selectedRequest.id) ? (
              <div className="self-approval-warning" style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', color: '#856404', marginBottom: '12px' }}>
                <AlertTriangle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                You cannot approve or deny your own time off request. Another manager must review this request.
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Note to Employee (optional)</label>
                  <textarea
                    value={managerNote}
                    onChange={(e) => setManagerNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                  />
                </div>
                <div className="action-buttons">
                  <button
                    className="btn btn-danger"
                    onClick={handleDeny}
                    disabled={processing}
                  >
                    <X size={16} /> Deny
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleApprove}
                    disabled={processing}
                  >
                    <Check size={16} /> Approve
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default TimeOffManager;
